import React, {useEffect, useState} from "react";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useMediaQuery,
    useTheme,
    Box,
    Autocomplete, Button, Alert, Typography
} from "@mui/material";
import Spinner from "../../spinner/Spinner";
import {Controller, useForm} from "react-hook-form";
import {subscriptionServices} from "../../../service/subscriptionService";
import {countries} from "../../utils/DataList";
import {SubscriptionDialog as Dialog} from "./SubscriptionDesign";

function UpdateBillingDetails(props) {
    const {onClose, open} = props;

    const {register, handleSubmit, control, setValue, formState: {errors}} = useForm();

    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleOnSubmit = async (data) => {
        setLoading(true);
        setErrorMsg("");
        let dataObj = {
            name: data?.name,
            address1: data?.address1,
            address2: data?.address2,
            country: data?.country?.code,
            city: data?.city,
            state: data?.state,
            zip: data?.zip,
            vatNumber: data?.vatNumber,
        };

        await subscriptionServices.updateBillingDetails(dataObj).then(() => {
            handleClose();
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setErrorMsg("");
        onClose();
    };

    useEffect(() => {
        if (open) {
            setLoading(true);
            subscriptionServices.getBillingDetails().then((result) => {
                setValue("name", result?.name);
                setValue("address1", result?.address1);
                setValue("address2", result?.address2);
                setValue("city", result?.city);
                setValue("state", result?.state);
                setValue("zip", result?.zip);
                setValue("vatNumber", result?.vatNumber);
                countries.forEach((item) => {
                    if (item.code === result?.country && item.code !== "") {
                        setValue("country", {code: item.code, label: item.label});
                    }
                });
            }).catch((error) => {
                setErrorMsg(error?.response?.data?.error);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [open, control, setValue]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth={true}
            fullScreen={fullScreen}
            aria-labelledby="update-billing-details"
        >
            <DialogTitle id="update-billing-details">Update billing details
                <Typography component="p" sx={{fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.75}}>Keep the company and address details on your invoices up to date.</Typography>
            </DialogTitle>
            {loading && <Spinner size={32} overComponentBox={true}/>}
            <DialogContent sx={{padding: "6px"}}>
                <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete={"off"}>
                    {errorMsg !== "" &&
                        <Alert severity="error" variant="filled" sx={{mb: 2}}>{errorMsg}</Alert>
                    }
                    <BillingDetailsFields register={register} control={control} errors={errors} disabled={loading}/>
                </form>
            </DialogContent>
            <DialogActions sx={{padding: 2}}>
                <Button type="button" color="inherit" variant="text" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button type="submit" color="primary" variant="contained" onClick={handleSubmit(handleOnSubmit)} disabled={loading}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function BillingDetailsFields({register, control, errors, disabled = false}) {
    const fields = [
        {name: 'name', label: 'Name / Company name *', placeholder: 'Enter your name or company name', full: true, required: true},
        {name: 'vatNumber', label: 'VAT number', placeholder: 'For EU business customers'},
        {name: 'address1', label: 'Address line 1', placeholder: 'Street address', full: true},
        {name: 'address2', label: 'Address line 2', placeholder: 'Apartment, suite, etc. (optional)', full: true},
        {name: 'city', label: 'City', placeholder: 'City'},
        {name: 'state', label: 'State / Region', placeholder: 'State or region'},
        {name: 'zip', label: 'Postal code', placeholder: 'Postal or ZIP code', full: true}
    ];
    const renderField = field => <TextField key={field.name} {...register(field.name, {required: !!field.required, maxLength: 100})}
        id={`billing-${field.name}`} name={field.name} error={!!errors?.[field.name]} disabled={disabled}
        helperText={errors?.[field.name] ? 'Please enter a valid value.' : undefined}
        label={field.label} placeholder={field.placeholder} variant="outlined" size="small" margin="dense" fullWidth
        slotProps={{inputLabel: {shrink: true}}} sx={{gridColumn: field.full ? '1 / -1' : 'auto'}}/>;
    return <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 1.5}}>
        {renderField(fields[0])}
        <BillingDetailsCountrySelect control={control} errors={errors} disabled={disabled}/>
        {fields.slice(1).map(renderField)}
    </Box>;
}

export function BillingDetailsCountrySelect({control, errors, disabled = false}) {
    let listOptions = [];
    countries?.forEach((item) => {
        if (item?.label !== "Global") {
            listOptions.push({code: item?.code, label: item?.label});
        }
    });

    return (
        <Controller
            render={({field: {onChange, ...props}}) => (
                <Autocomplete
                    id="billing-details-country-select"
                    disabled={disabled}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    getOptionLabel={(option) => option.label}
                    options={listOptions}
                    renderInput={params => (
                        <TextField
                            {...params}
                            error={!!errors?.country}
                            id="billing-details-country"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            placeholder="Select your country"
                            label="Country *"
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    )}
                    sx={{width: "100%"}}
                    onChange={(e, data) => onChange(data)}
                    {...props}
                />
            )}
            name="country"
            rules={{required: true}}
            defaultValue={null}
            control={control}
            onChange={([, data]) => data}
        />
    );
}

export default UpdateBillingDetails;